import { ref, onMounted } from 'vue'
import type { Ref } from 'vue'

import { getDocumentPreview } from './api'
import { documentPreviewToDocs } from './document-preview-to-docs'

interface InlinePreview {
  url?: string
  pdftronPath?: string
}

interface UseDocumentPreview {
  docs: Ref<string[]>
  pdftronPath: Ref<string | undefined>
  loading: Ref<boolean>
  error: Ref<boolean>
}

// Resolves a document's preview for PDFTron. When the server already baked the
// single previewable URL into the HTML (`inline`), it is used as-is — no
// request. Otherwise (a `.msg`, whose tabs require parsing the blob) the
// `#preview` payload is lazy-fetched on mount; a failed fetch sets `error` so
// the viewer can warn rather than sit blank.
export function useDocumentPreview(
  documentId: number,
  inline: InlinePreview = {}
): UseDocumentPreview {
  const docs = ref<string[]>(inline.url ? [inline.url] : [])
  const pdftronPath = ref<string | undefined>(
    inline.url ? inline.pdftronPath : undefined
  )
  const loading = ref(!inline.url)
  const error = ref(false)

  async function load(): Promise<void> {
    const response = await getDocumentPreview(documentId)

    if (response) {
      docs.value = documentPreviewToDocs(response)
      pdftronPath.value = response.pdftronPath
    } else {
      error.value = true
    }

    loading.value = false
  }

  if (!inline.url) {
    onMounted(load)
  }

  return { docs, pdftronPath, loading, error }
}
